import React, { FC, useEffect, useRef, useState } from "react";
import classNames from "classnames";

// Types
import { AppointmentInformationDto } from "@apiModels/AppointmentInformationDto.csharp";

// Components
import { FormAppointmentDetails } from '../../Booking/FormAppointmentDetails/FormAppointmentDetails';
import { PracticalInformation } from '../../Booking/PracticalInformation/PracticalInformation';
import { CenterInformation } from '../../Booking/CenterInformation/CenterInformation';
import { FAQAccordion } from '../../Booking/FAQAccordion/FAQAccordion';
import { ServiceContainerDto } from '@apiModels/ServiceContainerDto.csharp';

import { AppointmentCancellationConfirmationModal } from "../AppointmentCancellationConfirmationModal";

// Helpers
import { Errors } from "../Errors";
import { StepThreeParams } from "../../../Pages/MyAppointment/Steps/StepThreeParams.csharp";
import { Button } from '../../Booking/Button/Button';

interface StepThreeProps extends StepThreeParams {
  cancelAppointment: () => void;
  exit: () => void;
  appointmentInformation: AppointmentInformationDto;
  apiErrors: string[];
  isLoading: boolean;
  Modal: any;
}

export const AppointmentPanelStep: FC<StepThreeProps> = ({
  exitButtonText,
  cancelAppointmentModal,
  cancelButtonText,
  content,
  title,
  cancelAppointment,
  appointmentInformation,
  exit,
  apiErrors,
  isLoading,
  Modal,
}) => {
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const wrapperRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    Modal.setAppElement(wrapperRef.current!);
  }, []);

  const { cancellationEnabled, message: cancellationMessage } =
    appointmentInformation.cancellationDeadlineDto;

  const enableCancellation = cancellationEnabled && apiErrors.length == 0;

  const {
    doctor,
    location,
    appointment,
    service,
    specialization,
    bookingResponse,
    pricingSummary,
  } = appointmentInformation;

  return (
    <>
      <div className="appointment-panel-title-container">
        <div>
          <h1>{title}</h1>
        </div>
      </div>
      <div className="mb-4">
        <div className="mb-3">
          <div dangerouslySetInnerHTML={{ __html: content }} />
          <FormAppointmentDetails
            doctor={doctor}
            center={location}
            appointment={appointment}
            service={service as ServiceContainerDto}
            specializationName={specialization.name}
            displayHeading={false}
            pricingSummary={pricingSummary}
            cancellationMessage={cancellationMessage}
          />
          <PracticalInformation
            bookingResponse={bookingResponse}
            service={service as ServiceContainerDto}
            specializationName={specialization.name}
            isWaitlist={false}
            hideGeneralInfo
          />
          <CenterInformation
            centerName={location.name}
            bookingResponse={bookingResponse}
          />
          <FAQAccordion />
          <Errors errors={apiErrors} />
        </div>
        <div className="mt-3 d-flex flex-column accordion-faq accordion-faq--extended gap-2">
          <Button
            disabled={!enableCancellation}
            onClick={() => setIsModalOpen(true)}
            variant='primary'
            size='large'
          >
              {cancelButtonText}
          </Button>

          <Button
            onClick={exit}
            variant='secondary'
            size='large'
          >
              {exitButtonText}
          </Button>
        </div>
      </div>
      <AppointmentCancellationConfirmationModal
        Modal={Modal}
        isOpen={isModalOpen}
        modalContent={cancelAppointmentModal}
        onSubmit={cancelAppointment}
        onClose={() => setIsModalOpen(false)}
        isLoading={isLoading}
      />
    </>
  );
};
